import { ListFilterBar } from './data-view.js';
import { SegmentedControl, IconButton } from '@hue/design-system';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Card,
  FormErrorSummary,
  Input,
  Modal,
  StatusPill,
  useSnackbar,
} from '@hue/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Building2,
  ChevronRight,
  Clock3,
  KeyRound,
  LayoutGrid,
  Layers3,
  List,
  Package,
  Plus,
  Search,
  Store,
} from 'lucide-react';
import { useEffect, useState, type KeyboardEvent } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { getApiError, rootApi, type Tenant } from '../lib/api.js';
import { refreshSession } from '../lib/session.js';
import type { CompaniesView } from './list-views.js';
import type { ViewSetter } from './view-state.js';
import { getBrowserTimeZone } from '../lib/time-zone.js';
import { TenantSubscriptionDialog } from '../modules/billing/index.js';
import { TimeZoneSelect } from './time-zone-select.js';
import { CreateButton, PageHeader } from './page-title.js';

const tenantSchema = z.object({
  displayName: z.string().trim().min(2, 'Informe o nome do tenant.'),
  legalName: z.string().trim(),
  key: z
    .string()
    .trim()
    .regex(/^[a-z0-9](?:[a-z0-9-]{1,61}[a-z0-9])$/, 'Use letras minúsculas, números e hífens.'),
  unitDisplayName: z.string().trim().min(2, 'Informe o nome da unidade.'),
  unitKey: z
    .string()
    .trim()
    .regex(/^[a-z0-9](?:[a-z0-9-]{1,61}[a-z0-9])$/, 'Use letras minúsculas, números e hífens.'),
  timeZone: z.string().trim().min(1, 'Informe o fuso horário.'),
});
type TenantForm = z.infer<typeof tenantSchema>;
type TenantActivity = 'ALL' | 'ACTIVE' | 'INACTIVE';
type TenantLayout = 'cards' | 'list';

const statusLabels: Record<string, string> = {
  ACTIVE: 'Ativo',
  PROVISIONING: 'Provisionando',
  TRIAL_ACTIVE: 'Trial ativo',
  TRIAL_EXPIRED: 'Trial encerrado',
  PAST_DUE: 'Pagamento pendente',
  SUSPENDED: 'Suspenso',
  ARCHIVED: 'Arquivado',
};
const activityLabels: Record<TenantActivity, string> = {
  ALL: 'Todos',
  ACTIVE: 'Ativos',
  INACTIVE: 'Inativos',
};

const activeStatuses = new Set(['ACTIVE', 'TRIAL_ACTIVE']);

function isTenantActive(status: string): boolean {
  return activeStatuses.has(status);
}

function TenantStatus({ tenant }: { tenant: Tenant }) {
  return (
    <StatusPill tone={isTenantActive(tenant.status) ? 'positive' : 'neutral'} className="shrink-0">
      {statusLabels[tenant.status] ?? tenant.status}
    </StatusPill>
  );
}

function TenantEntry({
  tenant,
  layout,
  entering,
  disabled,
  onEnter,
  onOpenSubscription,
}: {
  tenant: Tenant;
  layout: TenantLayout;
  entering: boolean;
  disabled: boolean;
  onEnter: () => void;
  onOpenSubscription: () => void;
}) {
  // O card inteiro entra no tenant; a contratação é outra ação e não pode disparar aquela junto.
  const subscriptionButton = (
    <IconButton
      label={`Contratação de ${tenant.displayName}`}
      icon={<Package />}
      onClick={(event) => {
        event.stopPropagation();
        onOpenSubscription();
      }}
    />
  );

  const interactionProps = {
    role: 'button',
    tabIndex: disabled ? -1 : 0,
    'aria-label': `Gerenciar tenant ${tenant.displayName}`,
    'aria-disabled': disabled,
    onClick: () => {
      if (!disabled) onEnter();
    },
    onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
      if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        onEnter();
      }
    },
  } as const;

  if (layout === 'list') {
    return (
      <Card
        {...interactionProps}
        className={`group min-w-0 p-4 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 sm:p-5 ${
          disabled
            ? 'cursor-wait opacity-60'
            : 'cursor-pointer hover:border-brand-500/45 hover:bg-surface-elevated/45'
        }`}
      >
        <div className="flex flex-wrap items-center gap-3 sm:flex-nowrap sm:gap-4">
          <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-brand-500/10 text-brand-400">
            <Building2 className="size-5" />
          </span>
          <div className="min-w-0 flex-1 basis-[calc(100%-3.25rem)] sm:basis-auto">
            <div className="flex items-center gap-2">
              <h2 className="truncate text-base font-semibold tracking-[-.02em] sm:text-lg">
                {tenant.displayName}
              </h2>
              <span className="hidden sm:inline-flex">
                <TenantStatus tenant={tenant} />
              </span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-content-muted">
              <span className="min-w-0 break-all font-mono">{tenant.key}</span>
              <span className="flex items-center gap-1.5">
                <Layers3 className="size-3.5 text-brand-400" />
                {tenant.businessUnits.length}{' '}
                {tenant.businessUnits.length === 1 ? 'unidade' : 'unidades'}
              </span>
              <span className="flex min-w-0 items-center gap-1.5 break-all font-mono">
                <Clock3 className="size-3.5 shrink-0 text-brand-400" />
                {tenant.timeZone}
              </span>
            </div>
          </div>
          <span className="sm:hidden">
            <TenantStatus tenant={tenant} />
          </span>
          {subscriptionButton}
          <span className="hidden items-center gap-2 text-xs font-semibold text-brand-400 lg:flex">
            {entering ? 'Entrando…' : 'Gerenciar'}
            <ChevronRight className="size-4 transition group-hover:translate-x-0.5" />
          </span>
          <ChevronRight className="size-4 shrink-0 text-brand-400 transition group-hover:translate-x-0.5 lg:hidden" />
        </div>
      </Card>
    );
  }

  return (
    <Card
      {...interactionProps}
      className={`group min-h-56 border-surface-border bg-surface-card transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 ${
        disabled
          ? 'cursor-wait opacity-60'
          : 'cursor-pointer hover:border-brand-500/45 hover:bg-surface-elevated/45'
      }`}
    >
      <div className="mb-6 flex items-start justify-between">
        <span className="grid size-10 place-items-center rounded-xl bg-brand-500/10 text-brand-400">
          <Building2 className="size-5" />
        </span>
        <div className="flex items-center gap-2">
          <TenantStatus tenant={tenant} />
          {subscriptionButton}
        </div>
      </div>
      <h2 className="text-xl font-semibold tracking-[-.025em]">{tenant.displayName}</h2>
      <p className="mt-1 font-mono text-xs text-content-muted">{tenant.key}</p>
      <div className="mt-5 border-t border-surface-border pt-4">
        <div className="mb-4 flex items-center justify-between text-xs text-content-muted">
          <span className="flex items-center gap-2">
            <Layers3 className="size-3.5 text-brand-400" />
            {tenant.businessUnits.length}{' '}
            {tenant.businessUnits.length === 1 ? 'unidade' : 'unidades'}
          </span>
          <span className="flex items-center gap-2 font-mono">
            <Clock3 className="size-3.5 text-brand-400" />
            {tenant.timeZone}
          </span>
        </div>
        <span className="flex w-full items-center justify-between rounded-lg py-1 text-sm font-medium text-brand-400 transition group-hover:text-brand-300">
          {entering ? 'Entrando…' : 'Gerenciar tenant'}
          <ChevronRight className="size-4 transition group-hover:translate-x-0.5" />
        </span>
      </div>
    </Card>
  );
}

/// A lista de empresas da plataforma, em `/companies`.
///
/// Entrar numa empresa é pedido ao servidor, e é ele que troca o contexto da sessão: por isso o
/// destino aqui é o início, e não um endereço montado na mão. Quando a fatia 3 der endereço às
/// telas da empresa, este destino passa a ser `/companies/:key`.
export function ConsoleCompanies({
  view,
  onView,
}: {
  view: CompaniesView;
  onView: ViewSetter<CompaniesView>;
}) {
  const { search, activity, subscription } = view;
  const [formOpen, setFormOpen] = useState(false);
  const [enteringTenantId, setEnteringTenantId] = useState<string>();
  /// A busca espera 250 ms antes de virar pedido. Ela vai para a URL a cada letra — barato, é só
  /// trocar o endereço —, mas o servidor só é incomodado quando a pessoa para de digitar.
  const [debouncedSearch, setDebouncedSearch] = useState(() => search.trim());
  const [layout, setLayout] = useState<TenantLayout>(() =>
    localStorage.getItem('hue-tenant-view') === 'list' ? 'list' : 'cards',
  );
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const tenants = useQuery({
    queryKey: ['root', 'tenants', debouncedSearch, activity],
    queryFn: () =>
      rootApi.tenants({
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        activity,
      }),
    placeholderData: (previous) => previous,
  });
  /// A contratação aberta vem da URL, e por isso precisa ser resolvida a partir da chave — não do
  /// que está na lista. A lista pode estar filtrada, ou nem ter sido carregada ainda, e um link de
  /// contratação que só funciona quando a empresa por acaso aparece na página não é um link.
  const subscriptionTenant = useQuery({
    queryKey: ['root', 'tenant', subscription],
    queryFn: async () => {
      const matches = await rootApi.tenants({ search: subscription, activity: 'ALL' });
      return matches.find((candidate) => candidate.key === subscription) ?? null;
    },
    enabled: Boolean(subscription),
    retry: false,
  });
  const subscriptionTarget = subscription ? (subscriptionTenant.data ?? null) : null;

  /// Abrir é sempre a mesma coisa: pôr a chave no endereço. Quem clica na lista já tem a empresa
  /// em mãos, mas semear o cache com ela só adiantaria o primeiro quadro — a consulta acima
  /// revalida em seguida e é ela quem manda. Um caminho só é mais fácil de confiar do que dois que
  /// precisam concordar.
  function openSubscription(tenant: Tenant) {
    onView({ subscription: tenant.key });
  }

  function closeSubscription() {
    onView({ subscription: '' });
  }

  const form = useForm<TenantForm>({
    resolver: zodResolver(tenantSchema),
    defaultValues: {
      displayName: '',
      legalName: '',
      key: '',
      unitDisplayName: 'Unidade principal',
      unitKey: '',
      timeZone: getBrowserTimeZone(),
    },
  });
  const tenantTimeZone = useWatch({ control: form.control, name: 'timeZone' });
  const createTenant = useMutation({
    mutationFn: rootApi.createTenant,
    onSuccess: async (tenant) => {
      await queryClient.invalidateQueries({ queryKey: ['root', 'tenants'] });
      snackbar.success(
        `${tenant.displayName} foi provisionado com sua primeira unidade. Escolha o plano para liberar o que a empresa vai usar.`,
      );
      setFormOpen(false);
      // A empresa nasce sem contratação: sem plano, quem entrar nela só alcança o que é do núcleo
      // (pessoas, perfis, trilha). Emendar a contratação aqui é o que impede provisionar e esquecer.
      openSubscription(tenant);
    },
  });

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(timeout);
  }, [search]);

  function openTenantForm() {
    createTenant.reset();
    setFormOpen(true);
  }

  /// Cards ou lista é preferência de quem olha, não recorte do que está sendo olhado: fica na
  /// máquina, e não na URL. Mandar um link não deve impor a forma de ver de quem mandou.
  function changeLayout(next: TenantLayout) {
    setLayout(next);
    localStorage.setItem('hue-tenant-view', next);
  }

  function clearFilters() {
    setDebouncedSearch('');
    onView({ search: '', activity: 'ALL' });
  }

  async function enterTenant(tenant: Tenant) {
    setEnteringTenantId(tenant.id);
    try {
      await rootApi.enterTenant(tenant.id);
      await refreshSession(queryClient);
      // Com uma unidade ativa não há o que escolher: a tela de Unidades só atrasaria a entrada.
      const hasUnitChoice = tenant.businessUnits.filter((unit) => unit.active).length > 1;
      await navigate({
        to: hasUnitChoice ? '/companies/$companyKey/settings/units' : '/companies/$companyKey',
        params: { companyKey: tenant.key },
      });
    } catch (error) {
      snackbar.failure(getApiError(error));
    } finally {
      setEnteringTenantId(undefined);
    }
  }

  const hasFilters = Boolean(search.trim()) || activity !== 'ALL';

  return (
    <>
      <PageHeader
        trail="Plataforma / Tenants"
        title="Tenants"
        description="Empresas com dados, unidades e acessos isolados."
        actions={<CreateButton onClick={openTenantForm}>Novo tenant</CreateButton>}
      />

      <section aria-label="Filtros de tenants">
        <ListFilterBar
          showViewPicker={false}
          searchLabel="Buscar tenant"
          searchPlaceholder="Nome ou chave do tenant"
          search={search}
          onSearchChange={(search) => onView({ search }, { replace: true })}
          count={
            <>
              {tenants.data?.length ?? 0}{' '}
              {(tenants.data?.length ?? 0) === 1 ? 'tenant encontrado' : 'tenants encontrados'}
              {tenants.isFetching ? ' · Atualizando…' : ''}
            </>
          }
        >
          <div>
            <SegmentedControl
              label="Filtrar tenants por status"
              compact
              value={activity}
              onChange={(activity) => onView({ activity })}
              options={(Object.keys(activityLabels) as TenantActivity[]).map((value) => ({
                value,
                label: activityLabels[value],
              }))}
            />
          </div>

          <div>
            <SegmentedControl
              label="Modo de visualização"
              compact
              value={layout}
              onChange={changeLayout}
              options={[
                {
                  value: 'cards',
                  label: <LayoutGrid className="mx-auto size-4" />,
                  accessibleLabel: 'Visualização em cards',
                },
                {
                  value: 'list',
                  label: <List className="mx-auto size-4" />,
                  accessibleLabel: 'Visualização em lista',
                },
              ]}
            />
          </div>
        </ListFilterBar>
      </section>

      {tenants.isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <div className="h-52 animate-pulse rounded-2xl bg-surface-card" />
          <div className="h-52 animate-pulse rounded-2xl bg-surface-card" />
        </div>
      ) : tenants.error ? (
        <Card className="max-w-md">
          <p role="alert" className="text-sm text-danger">
            {getApiError(tenants.error)}
          </p>
          <Button className="mt-4" variant="outline" onClick={() => void tenants.refetch()}>
            Tentar novamente
          </Button>
        </Card>
      ) : tenants.data?.length ? (
        <div
          className={layout === 'cards' ? 'grid gap-4 md:grid-cols-2 xl:grid-cols-3' : 'grid gap-3'}
        >
          {tenants.data.map((tenant) => (
            <TenantEntry
              key={tenant.id}
              tenant={tenant}
              layout={layout}
              entering={enteringTenantId === tenant.id}
              disabled={Boolean(enteringTenantId)}
              onEnter={() => void enterTenant(tenant)}
              onOpenSubscription={() => openSubscription(tenant)}
            />
          ))}
        </div>
      ) : hasFilters ? (
        <Card className="max-w-lg py-10 text-center">
          <span className="mx-auto mb-4 grid size-12 place-items-center rounded-2xl bg-brand-500/10 text-brand-400">
            <Search className="size-5" />
          </span>
          <h2 className="text-lg font-semibold">Nenhum tenant encontrado</h2>
          <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-content-muted">
            Revise o nome, a chave ou o filtro de atividade.
          </p>
          <Button className="mt-6" variant="outline" onClick={clearFilters}>
            Limpar filtros
          </Button>
        </Card>
      ) : (
        <Card className="max-w-md py-10 text-center">
          <span className="mx-auto mb-4 grid size-12 place-items-center rounded-2xl bg-brand-500/10 text-brand-400">
            <Building2 className="size-5" />
          </span>
          <h2 className="text-lg font-semibold">Nenhum tenant cadastrado</h2>
          <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-content-muted">
            Comece criando a primeira empresa e a unidade dela.
          </p>
          <Button className="mt-6" onClick={openTenantForm}>
            <Plus className="size-4" />
            Criar tenant
          </Button>
        </Card>
      )}

      {subscriptionTarget ? (
        <TenantSubscriptionDialog
          tenantId={subscriptionTarget.id}
          tenantDisplayName={subscriptionTarget.displayName}
          onClose={closeSubscription}
          onApplied={async (applied) => {
            await queryClient.invalidateQueries({ queryKey: ['root', 'subscription'] });
            snackbar.success(
              `${applied.tenantDisplayName} passou a usar o plano ${applied.planDisplayName}.`,
            );
            closeSubscription();
          }}
        />
      ) : null}

      <Modal
        open={formOpen}
        onOpenChange={setFormOpen}
        title="Novo tenant"
        description="Crie a empresa e a primeira unidade operacional."
      >
        <form
          noValidate
          onSubmit={(event) => void form.handleSubmit((data) => createTenant.mutate(data))(event)}
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <Input
              label="Nome do tenant"
              placeholder="Ex.: Clínica Aurora"
              error={form.formState.errors.displayName?.message}
              {...form.register('displayName')}
            />
            <Input
              label="Chave única"
              placeholder="Ex.: clinica-aurora"
              leftIcon={<KeyRound className="size-4" />}
              error={form.formState.errors.key?.message}
              {...form.register('key')}
            />
            <Input
              className="sm:col-span-2"
              label="Razão social (opcional)"
              placeholder="Nome empresarial completo"
              error={form.formState.errors.legalName?.message}
              {...form.register('legalName')}
            />
            <Input
              label="Primeira unidade"
              placeholder="Ex.: Unidade principal"
              leftIcon={<Store className="size-4" />}
              error={form.formState.errors.unitDisplayName?.message}
              {...form.register('unitDisplayName')}
            />
            <Input
              label="Chave da unidade"
              placeholder="Ex.: clinica-aurora-centro"
              error={form.formState.errors.unitKey?.message}
              {...form.register('unitKey')}
            />
            <div className="sm:col-span-2">
              <TimeZoneSelect
                label="Fuso operacional"
                value={tenantTimeZone}
                onValueChange={(timeZone) =>
                  form.setValue('timeZone', timeZone, { shouldValidate: true })
                }
                error={form.formState.errors.timeZone?.message}
                showTechnicalDetails
              />
              <p className="mt-1 text-[11px] leading-5 text-content-muted">
                Usado em fechamentos, vencimentos e agendamentos do tenant.
              </p>
            </div>
          </div>
          {createTenant.error ? (
            <div
              role="alert"
              className="mt-5 rounded-xl border border-danger/35 bg-danger/10 px-4 py-3 text-sm text-danger"
            >
              {getApiError(createTenant.error)}
            </div>
          ) : null}
          <div className="mt-7 flex flex-col-reverse gap-3 border-t border-surface-border pt-5 sm:flex-row sm:justify-end">
            <FormErrorSummary className="order-last sm:order-first sm:mr-auto sm:flex-1" />
            <Button
              className="w-full sm:w-auto"
              type="button"
              variant="outline"
              onClick={() => setFormOpen(false)}
            >
              Cancelar
            </Button>
            <Button className="w-full sm:w-auto" loading={createTenant.isPending}>
              Criar tenant e unidade
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
